import { theme } from '@Guidelines/Theme/theme';
import type DocumentSettingsFormData from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/FormData/DocumentSettingsFormData';
import HintText from '@Guidelines/Component/HintText';
import { css } from '@emotion/react';
import * as React from 'react';
import { InputNumber, TextArea } from '@verclam/ui-kit';

const detailsStyle = css`
	padding: 1.125rem 1.375rem;
	border-radius: ${theme.radii.xlarge};
	border: 1.5px solid ${theme.colors.border};
	background-color: ${theme.colors.surface};
`;

const summaryStyle = css`
	${theme.typography.eyebrow}
	cursor: pointer;
	text-transform: uppercase;
	color: ${theme.colors.inkMuted};
	line-height: 1.6;
`;

const contentStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.875rem;
	padding-top: 0.875rem;
`;

const temperatureStyle = css`
	max-width: 13.75rem;
`;

type Props = {
	readonly settings: DocumentSettingsFormData;
	readonly onChange: React.Dispatch<React.SetStateAction<DocumentSettingsFormData>>;
};

/** Temperature and specific vocabulary, folded away; both fall back to the inherited value when empty. */
const AdvancedSection: React.FC<Props> = ({ settings, onChange }) => {
	const handleTemperatureChange = React.useCallback(
		(value: number | null) => {
			onChange((current) => current.setTemperature(value));
		},
		[onChange],
	);

	const handleVocabularyChange = React.useCallback(
		(value: string) => {
			onChange((current) => current.setVocabularyText(value));
		},
		[onChange],
	);

	return (
		<details css={detailsStyle}>
			<summary css={summaryStyle}>Réglages avancés</summary>
			<div css={contentStyle}>
				<div css={temperatureStyle}>
					<InputNumber
						name="temperature"
						label="Température"
						placeholder="Héritée"
						min={0}
						max={2}
						step={0.1}
						maxFractionDigits={2}
						value={settings.temperature}
						onChange={handleTemperatureChange}
						errorMessage={settings.getValidationMessage('temperature')}
						isFormFieldValid={settings.isValid('temperature')}
					/>
				</div>
				<TextArea
					name="vocabularyText"
					label="Vocabulaire spécifique"
					subLabel="Un terme par ligne."
					rows={2}
					placeholder="Hérité"
					value={settings.vocabularyText}
					onChange={handleVocabularyChange}
				/>
				<HintText>Laissés vides, ces réglages reprennent la valeur du compte.</HintText>
			</div>
		</details>
	);
};

export default AdvancedSection;
